import type { IBillItem, IBillItemOption, ISelectedItem, IUserItemSelection } from "@/data/model/Bill";
import { Format } from "@/data/util/Format";

import { renderItemIconsHtml } from "@/shared/helpers/AttributeHelper";
import { clientLanguage } from "@/shared/localization/language";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";

export class LineItem {
    public $element: JQuery;
    private readonly $text: JQuery;
    private readonly $variants: JQuery;
    private readonly $commentContainer: JQuery;
    private readonly $selection: JQuery;
    private readonly $quantity: JQuery;
    private $price?: JQuery;

    public model?: IBillItem;
    private mySelectedQuantity = 0;
    private remainingQuantity = 0;
    public isFullyPaid = false;
    private selectionModeActive = false;

    public onSelect?: { () };

    constructor() {
        this.$element = $('<div class="lineitem selectable" />');
        Ripple.bindRippleOut(this.$element, undefined, 0.18);

        const inner = $('<div class="inner" />').appendTo(this.$element);

        this.$quantity = $('<div class="quantity">').appendTo(inner);

        const container = $('<div class="textcontainer" />').appendTo(inner);

        this.$text = $('<div class="text" />').appendTo(container);
        this.$variants = $('<div class="variants" />').appendTo(container);
        this.$commentContainer = $('<div class="comment-container" />').appendTo(container);
        this.$selection = $('<div class="selection"><div class="circle" /></div>').appendTo(this.$element);

        this.$element.click((e) => {
            e.preventDefault();

            if (this.isFullyPaid) return;

            if ($(e.currentTarget).hasClass("selectable") && !$(e.currentTarget).hasClass("complete"))
                this.itemClicked();
        });
    }

    public update(model: IBillItem): LineItem {
        this.$text.empty();
        this.$variants.empty();
        this.$commentContainer.empty();
        this.$quantity.empty();

        this.model = model;
        this.calcRemainingQuantity();
        this.$element.data("sortOrder", this.model.sortOrder);

        if (model.name !== undefined) {
            const itmPrice =
                this.mySelectedQuantity > 0
                    ? this.mySelectedQuantity * model.unitPrice
                    : this.remainingQuantity * model.unitPrice;

            this.$text.append(
                '<span class="col name">' +
                    Format.translate(model.name, clientLanguage()) +
                    renderItemIconsHtml(model.articleId) +
                    "</span>"
            );
            this.$quantity.append(this.remainingQuantity + "x");
            this.$price = $(
                '<span class="col lineprice">' +
                    (itmPrice != null && itmPrice > 0 ? itmPrice.toFixed(2) : "") +
                    "</span>"
            ).appendTo(this.$text);
        }

        if (model.options.length > 0) {
            this.$variants.show();
            this.recursiveAppendOptions(this.$variants, model, this.remainingQuantity, this.selectionModeActive);
        } else {
            this.$variants.hide();
        }

        if (model.comment) {
            const isUserOperator = TastierClient.identityContext.isWaiterOrAdmin();

            // show comment if user is operator or comment is for the current end user
            if (isUserOperator || model.comment.identityId === TastierClient.identityContext.getIdentityId()) {
                const comment = $('<div class="comment" />').appendTo(this.$commentContainer);
                comment.append('<div class="comment-text">' + Format.htmlEscape(model.comment.text) + "</div>");

                if (isUserOperator) {
                    comment.addClass("operator-comment");
                }
            }
        }

        this.redrawSelection();

        if (this.remainingQuantity <= 0) {
            this.$element.addClass("complete");
        } else {
            this.$element.removeClass("complete");
        }

        return this;
    }

    private recursiveAppendOptions(
        $container: JQuery,
        item: IBillItem,
        multiplier: number,
        selectionModeActive: boolean
    ) {
        if (item.options.length === 0) return;

        const subContainer = $('<div class="subs" />').appendTo($container);

        for (const i in item.options) {
            const optionGroup = item.options[i];
            const groupContainer = $(
                '<div class="group"><div class="name">' +
                    Format.htmlEscape(Format.translate(optionGroup.name, clientLanguage())) +
                    "</div></div>"
            ).appendTo(subContainer);
            const optionsContainer = $('<div class="entries" />').appendTo(groupContainer);

            for (const ai in optionGroup.items) {
                const optionContainer = $('<div class="item" />').appendTo(optionsContainer);
                const optionHead = $('<div class="head" />').appendTo(optionContainer);

                const option = optionGroup.items[ai];
                const nextMultiplier = multiplier * option.quantity;

                optionHead.append(
                    '<div class="name">' +
                        (option.quantity === 1 ? "" : '<div class="quantity">' + option.quantity + "x</div>") +
                        Format.htmlEscape(Format.translate(option.name, clientLanguage())) +
                        renderItemIconsHtml(option.articleId) +
                        "</div>"
                );
                const totalAmount = $(
                    '<div class="price">' +
                        (option.unitPrice === 0 ? "" : (nextMultiplier * option.unitPrice).toFixed(2)) +
                        "</div>"
                ).appendTo(optionHead);

                if (selectionModeActive && this.mySelectedQuantity === 0) {
                    totalAmount.css("color", "#a0a0a0");
                }
                this.recursiveAppendItemOptions(optionContainer, option, nextMultiplier, selectionModeActive);
            }
        }
    }

    private recursiveAppendItemOptions(
        $container: JQuery,
        item: IBillItemOption,
        multiplier: number,
        selectionModeActive: boolean
    ) {
        if (item.options.length === 0) return;

        const subContainer = $('<div class="subs" />').appendTo($container);

        for (const i in item.options) {
            const optionGroup = item.options[i];
            const groupContainer = $(
                '<div class="group"><div class="name">' +
                    Format.htmlEscape(Format.translate(optionGroup.name, clientLanguage())) +
                    "</div></div>"
            ).appendTo(subContainer);
            const optionsContainer = $('<div class="entries" />').appendTo(groupContainer);

            for (const ai in optionGroup.items) {
                const optionContainer = $('<div class="item" />').appendTo(optionsContainer);
                const optionHead = $('<div class="head" />').appendTo(optionContainer);

                const option = optionGroup.items[ai];
                const nextMultiplier = multiplier * option.quantity;

                optionHead.append(
                    '<div class="name">' +
                        (option.quantity === 1 ? "" : '<div class="quantity">' + option.quantity + "x</div>") +
                        Format.htmlEscape(Format.translate(option.name, clientLanguage())) +
                        renderItemIconsHtml(option.articleId) +
                        "</div>"
                );
                const totalAmount = $(
                    '<div class="price">' +
                        (option.unitPrice === 0 ? "" : (nextMultiplier * option.unitPrice).toFixed(2)) +
                        "</div>"
                ).appendTo(optionHead);

                if (selectionModeActive && this.mySelectedQuantity === 0) {
                    totalAmount.css("color", "#a0a0a0");
                }
                this.recursiveAppendItemOptions(optionContainer, option, nextMultiplier, selectionModeActive);
            }
        }
    }

    public remove() {
        this.$element.slideUp(300, "easeOutExpo", () => {
            this.$element.remove();
        });
    }

    public updateSelectionMode(selectionModeActive: boolean) {
        this.selectionModeActive = selectionModeActive;
        if (selectionModeActive && this.mySelectedQuantity === 0) {
            this.$price!.css("color", "#a0a0a0");
        } else {
            this.$price!.css("color", "#484848");
        }

        this.$variants.empty();
        if (this.model!.options.length > 0) {
            this.$variants.show();
            this.recursiveAppendOptions(
                this.$variants,
                this.model!,
                this.mySelectedQuantity > 0 ? this.mySelectedQuantity : this.remainingQuantity,
                selectionModeActive
            );
        } else {
            this.$variants.hide();
        }
    }

    public setSelection(selections: Array<IUserItemSelection>, redraw?: boolean): LineItem {
        this.mySelectedQuantity = 0;

        for (const i in selections) {
            const sel = selections[i];
            this.mySelectedQuantity = sel.quantity;
        }

        this.calcRemainingQuantity();

        if (redraw === undefined || redraw !== false) {
            this.calcRemainingQuantity();

            this.redrawSelection();

            if (this.remainingQuantity <= 0) this.$element.addClass("complete");
            else this.$element.removeClass("complete");
        }

        return this;
    }

    public getSelection(): ISelectedItem | undefined {
        if (this.mySelectedQuantity === 0) return undefined;

        return {
            quantity: this.mySelectedQuantity,
            billItemId: this.model!.id,
        };
    }

    public clearSelection(): LineItem {
        this.mySelectedQuantity = 0;
        this.redrawSelection();
        return this;
    }

    private calcRemainingQuantity() {
        if (this.model === undefined) {
            this.remainingQuantity = 0;
            this.isFullyPaid = false;
            return;
        }

        this.isFullyPaid = false; //paidQty >= this.model.quantity;
        this.remainingQuantity = this.model.unpaidQuantity;
    }

    private itemClicked() {
        const initial = this.mySelectedQuantity;
        this.mySelectedQuantity++;
        if (this.mySelectedQuantity > this.remainingQuantity) this.mySelectedQuantity = 0;

        if (initial !== this.mySelectedQuantity) {
            this.redrawSelection();

            if (this.onSelect !== undefined) this.onSelect();
        }
    }

    private redrawSelection() {
        const itmPrice =
            this.mySelectedQuantity > 0
                ? this.mySelectedQuantity * (this.model?.unitPrice ?? 0)
                : this.remainingQuantity * (this.model?.unitPrice ?? 0);
        this.$price!.text(itmPrice != null && itmPrice > 0 ? itmPrice.toFixed(2) : "");

        this.$variants.empty();
        if (this.model!.options.length > 0) {
            this.$variants.show();
            this.recursiveAppendOptions(
                this.$variants,
                this.model!,
                this.mySelectedQuantity > 0 ? this.mySelectedQuantity : this.remainingQuantity,
                this.selectionModeActive
            );
        } else {
            this.$variants.hide();
        }

        const circle = this.$selection.find("> .circle");
        let badge = this.$selection.find("> .badge");

        if (this.mySelectedQuantity > 0) {
            if (!circle.hasClass("active")) {
                circle.addClass("active");

                const check = $('<i class="fas fa-check"></i>').appendTo(circle);
                check[0].offsetHeight;
                check.addClass("circlein");

                if (this.model!.quantity > 1) {
                    badge = $('<div class="badge" />').appendTo(this.$selection);
                    badge[0].offsetHeight;
                    badge.addClass("shown");
                }
            }

            badge.html(this.mySelectedQuantity + "");
        } else {
            if (circle.hasClass("active")) {
                circle.removeClass("active");
                const check = circle.find("> .fas").removeClass("circlein");
                check.delay(180).queue((e) => {
                    check.remove();
                    e();
                });

                badge
                    .removeClass("shown")
                    .delay(180)
                    .queue((e) => {
                        badge.remove();
                        e();
                    });
            }
        }
    }

    public appendTo($element: JQuery): LineItem {
        this.$element.appendTo($element);

        return this;
    }
}
